import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * LeadsNewTab — "Leady - nové"
 * Přehled nákladů obchodníka za leady v daném měsíci (rozpad po šaržích + celkem).
 * Admin může vybrat uživatele a zapnout/vypnout účtování leadů (propíše se do shrnutí a faktury).
 * Neadmin vidí pouze sebe.
 */
export default function LeadsNewTab({ userRole, users, currentUserId, isDarkMode }) {
  const isAdmin = userRole === 'admin' || userRole === 'callcentrum';
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedUser, setSelectedUser] = useState(isAdmin ? '' : currentUserId);
  const [data, setData] = useState({ chargeEnabled: null, totalCost: 0, byBatch: [], leads: [] });
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);

  const availableUsers = isAdmin ? (users || []) : [];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/leads-cost?month=${selectedMonth}&userId=${currentUserId || ''}`;
      const effectiveUser = isAdmin ? selectedUser : currentUserId;
      if (effectiveUser) url += `&filterUserId=${effectiveUser}`;
      const res = await fetch(url);
      const json = res.ok ? await res.json() : { chargeEnabled: null, totalCost: 0, byBatch: [], leads: [] };
      setData(json);
    } catch {
      setData({ chargeEnabled: null, totalCost: 0, byBatch: [], leads: [] });
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedUser, currentUserId, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggleCharge = async () => {
    if (!isAdmin) return;
    setToggling(true);
    try {
      const res = await fetch(`${API_URL}/leads-charge-config?userId=${currentUserId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !data.chargeEnabled, actorUserId: currentUserId })
      });
      if (res.ok) {
        const json = await res.json();
        setData(d => ({ ...d, chargeEnabled: json.leadsChargeEnabled }));
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Chyba při změně účtování leadů');
      }
    } catch {
      alert('Chyba připojení');
    } finally {
      setToggling(false);
    }
  };

  const th = `px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`;
  const td = `px-3 py-2 text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`;
  const tdMuted = `px-3 py-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`;
  const card = `rounded-lg border p-4 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} shadow-sm`;

  const activeLeads = data.leads.filter(l => !l.excluded);
  const excludedLeads = data.leads.filter(l => l.excluded);
  const settlements = data.settlements || [];
  const settlementTotal = settlements.reduce((s, x) => s + (x.credit || 0), 0); // ≤ 0

  return (
    <div>
      {/* Filtry */}
      <div className={`flex flex-wrap gap-4 items-end mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
        <div>
          <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Měsíc</label>
          <input
            type="month"
            value={selectedMonth}
            onChange={e => setSelectedMonth(e.target.value)}
            className={`border rounded px-2 py-1 text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
          />
        </div>

        {isAdmin && (
          <div>
            <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Uživatel</label>
            <select
              value={selectedUser}
              onChange={e => setSelectedUser(e.target.value)}
              className={`border rounded px-2 py-1 text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
            >
              <option value="">— Vyberte uživatele —</option>
              {availableUsers.map(u => (
                <option key={u._id} value={u._id}>{u.name || u.email}</option>
              ))}
            </select>
          </div>
        )}

        {/* Přepínač účtování leadů (admin) — globální pro celou komponentu */}
        {isAdmin && (
          <div
            className={`flex items-center gap-2 ${toggling ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
            onClick={handleToggleCharge}
            title="Zapnout/vypnout účtování nákladů za leady pro celý systém provizí"
          >
            <div className={`relative w-11 h-6 rounded-full transition-colors ${data.chargeEnabled ? 'bg-green-500' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}>
              <span className={`absolute left-0.5 top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${data.chargeEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
            </div>
            <span className={`text-sm font-semibold ${data.chargeEnabled ? 'text-green-500' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Účtovat leady: {data.chargeEnabled ? 'ZAP' : 'VYP'}
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div className={`text-center py-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Načítám…</div>
      ) : (isAdmin && !selectedUser) ? (
        <div className={`text-center py-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Vyberte uživatele pro zobrazení nákladů za leady.</div>
      ) : (
        <div className="space-y-6">
          {/* Souhrn po šaržích */}
          <div className={card}>
            <h3 className={`font-semibold text-sm mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>💸 Náklady za leady tento měsíc</h3>
            {data.byBatch.length === 0 ? (
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Žádné účtovatelné leady v tomto měsíci.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <th className={th}>Šarže</th>
                      <th className={th}>Počet</th>
                      <th className={th}>Cena / lead</th>
                      <th className={th}>Celkem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byBatch.map(b => (
                      <tr key={b.batchName} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                        <td className={`${td} font-medium`}>{b.batchName}</td>
                        <td className={td}>{b.count}</td>
                        <td className={tdMuted}>{(b.price || 0).toLocaleString('cs-CZ')} Kč</td>
                        <td className={`${td} font-semibold`}>{(b.total || 0).toLocaleString('cs-CZ')} Kč</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className={`border-t-2 ${isDarkMode ? 'border-gray-500' : 'border-gray-300'}`}>
                      <td className={`${td} font-bold`}>Celkem</td>
                      <td className={`${td} font-bold`}>{activeLeads.length}</td>
                      <td className={td}></td>
                      <td className={`${td} font-bold text-blue-500`}>{(data.totalCost || 0).toLocaleString('cs-CZ')} Kč</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
            {isAdmin && (
              <p className={`text-xs mt-3 ${data.chargeEnabled ? 'text-green-500' : (isDarkMode ? 'text-gray-400' : 'text-gray-500')}`}>
                {data.chargeEnabled
                  ? '✔ Náklady za leady se propisují do shrnutí a faktury (odečteno z výplaty).'
                  : '○ Účtování leadů je vypnuté — do shrnutí ani faktury se nepropisuje.'}
              </p>
            )}
          </div>

          {/* Seznam leadů */}
          <div className={card}>
            <h3 className={`font-semibold text-sm mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              📋 Převzaté leady ({activeLeads.length})
            </h3>
            {activeLeads.length === 0 ? (
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Žádné leady v tomto měsíci.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      {isAdmin && <th className={th}>Uživatel</th>}
                      <th className={th}>Název zákazníka</th>
                      <th className={th}>IČO</th>
                      <th className={th}>Šarže</th>
                      <th className={th}>Datum</th>
                      <th className={th}>Forecast</th>
                      <th className={th}>Cena</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeLeads.map((lead, idx) => (
                      <tr key={lead.transferId || idx} className={`border-b ${isDarkMode ? 'border-gray-700 hover:bg-gray-600' : 'border-gray-100 hover:bg-gray-50'}`}>
                        {isAdmin && <td className={tdMuted}>{lead.toUserName || '—'}</td>}
                        <td className={`${td} font-medium`}>{lead.companyName || '—'}</td>
                        <td className={tdMuted}>{lead.companyIco || '—'}</td>
                        <td className={`${tdMuted} max-w-[160px] truncate`} title={lead.batchName}>{lead.batchName || '—'}</td>
                        <td className={tdMuted}>{lead.transferDate ? new Date(lead.transferDate).toLocaleDateString('cs-CZ') : '—'}</td>
                        <td className={tdMuted}>{lead.forecast || '—'}</td>
                        <td className={`${td} font-semibold`}>{(lead.price || 0).toLocaleString('cs-CZ')} Kč</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Vyloučené leady (vrácené / mimo statistiku) */}
          {excludedLeads.length > 0 && (
            <div className={card}>
              <h3 className={`font-semibold text-sm mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                🚫 Nezapočtené leady (vrácené / mimo statistiku) ({excludedLeads.length})
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <th className={th}>Název zákazníka</th>
                      <th className={th}>IČO</th>
                      <th className={th}>Šarže</th>
                      <th className={th}>Cena (nezapočteno)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {excludedLeads.map((lead, idx) => (
                      <tr key={lead.transferId || idx} className={`border-b line-through ${isDarkMode ? 'border-gray-700 text-gray-500' : 'border-gray-100 text-gray-400'}`}>
                        <td className="px-3 py-2 text-sm font-medium">{lead.companyName || '—'}</td>
                        <td className="px-3 py-2 text-sm">{lead.companyIco || '—'}</td>
                        <td className="px-3 py-2 text-sm">{lead.batchName || '—'}</td>
                        <td className="px-3 py-2 text-sm">{(lead.price || 0).toLocaleString('cs-CZ')} Kč</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Vrácení z uznaných reklamací (vyrovnání naplánované na tento měsíc) */}
          {settlements.length > 0 && (
            <div className={card}>
              <h3 className={`font-semibold text-sm mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                ↩️ Vrácení z reklamací tento měsíc ({settlements.length})
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <th className={th}>Název zákazníka</th>
                      <th className={th}>IČO</th>
                      <th className={th}>Šarže</th>
                      <th className={th}>Plátce</th>
                      <th className={th}>Vrácení</th>
                    </tr>
                  </thead>
                  <tbody>
                    {settlements.map((s, idx) => (
                      <tr key={s.complaintId || idx} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                        <td className={`${td} font-medium`}>{s.companyName || '—'}</td>
                        <td className={tdMuted}>{s.companyIco || '—'}</td>
                        <td className={`${tdMuted} max-w-[160px] truncate`} title={s.batchName}>{s.batchName || '—'}</td>
                        <td className={tdMuted}>{s.payer === 'partner' ? 'Partner (naše chyba)' : 'Callcentrum'}</td>
                        <td className="px-3 py-2 text-sm font-semibold text-green-500">{(s.credit || 0).toLocaleString('cs-CZ')} Kč</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className={`border-t-2 ${isDarkMode ? 'border-gray-500' : 'border-gray-300'}`}>
                      <td className={`${td} font-bold`} colSpan={4}>Celkem vráceno</td>
                      <td className={`${td} font-bold text-green-500`}>{settlementTotal.toLocaleString('cs-CZ')} Kč</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
